import {
  BellIcon,
  CaretDownIcon,
  ListMagnifyingGlassIcon,
  SignOutIcon,
} from '@phosphor-icons/react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useLogout, type AuthUser } from '@entities/user/api';
import { ROUTES } from '@shared/config/routes';

import styles from './AccountMenu.module.css';

/** Пунктов три: «Meine Bestellungen», каналы уведомлений, выход. */
const LAST_INDEX = 2;

/** Первая буква имени или адреса — аватар; кабинет с профилем приходит с Epic 28. */
function initial(user: AuthUser): string {
  const source = user.display_name?.trim() || user.email || '?';
  return source.charAt(0).toUpperCase();
}

interface AccountMenuProps {
  user: AuthUser;
  /** На тёмной шапке — публичной и приложения: тёмные кнопка и меню (DESIGN_SYSTEM §4.3). */
  inverse?: boolean | undefined;
}

/**
 * Меню аккаунта в шапке.
 *
 * Поведение — кнопка меню WAI-ARIA, как у выбора языка рядом: при открытии
 * фокус встаёт на первый пункт (стрелкой вверх — на последний), стрелки ходят
 * по кругу, Home и End — к краям, Enter и пробел выбирают, Escape закрывает и
 * возвращает фокус на кнопку, Tab и клик снаружи — просто закрывают.
 */
export function AccountMenu({ user, inverse = false }: AccountMenuProps) {
  const { t } = useTranslation();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  // Какой пункт держит фокус, пока меню открыто: фокус двигает эффект ниже,
  // клавиши меняют только индекс.
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const triggerId = useId();
  const menuId = useId();

  const openAt = (index: number) => {
    setActiveIndex(index);
    setOpen(true);
  };

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (open) itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  // Клик снаружи и Escape. Escape слушается на документе: меню, открытое
  // мышью, закрывается им и тогда, когда фокус остался на кнопке.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Фокус возвращается на кнопку, только если он был внутри меню: иначе
      // Escape уводил бы его оттуда, где человек сейчас находится.
      const focusInside = containerRef.current?.contains(document.activeElement) ?? false;
      setOpen(false);
      if (focusInside) triggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    // Enter и пробел открывают меню обычным кликом; стрелки — здесь.
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openAt(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(LAST_INDEX);
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => (index === LAST_INDEX ? 0 : index + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => (index === 0 ? LAST_INDEX : index - 1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(LAST_INDEX);
        break;
      case 'Tab':
        // Фокус уходит своим путём, меню за ним не остаётся висеть.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const itemRef = (index: number) => (node: HTMLElement | null) => {
    itemRefs.current[index] = node;
  };

  const label = user.email ?? t('auth.account.noEmail');

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        className={[styles.trigger, inverse && styles.triggerInverse].filter(Boolean).join(' ')}
        // На узком экране адрес скрыт и остаётся только буква-аватар, которая
        // спрятана от скринридеров. Без явной подписи кнопка осталась бы вовсе
        // без доступного имени.
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          if (open) close(true);
          else openAt(0);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initial(user)}
        </span>
        <span className={styles.email}>{label}</span>
        <CaretDownIcon
          size={14}
          weight="bold"
          aria-hidden="true"
          className={[styles.caret, open && styles.caretOpen].filter(Boolean).join(' ')}
        />
      </button>

      {open ? (
        <div
          id={menuId}
          className={[styles.menu, inverse && styles.menuInverse].filter(Boolean).join(' ')}
          role="menu"
          aria-labelledby={triggerId}
          onKeyDown={onMenuKeyDown}
        >
          {/* Пункта «Профиль» нет намеренно: экрана кабинета до Epic 28 не
              существует, а ссылка на заглушку обещала бы отсутствующую функцию
              (CLAUDE.md §2). Каналы уведомлений (FE-160) — экран вне воронки
              заказа, поэтому живёт в меню аккаунта, а не в трекере шагов. */}
          {/* «Meine Bestellungen» — единственный путь к заказам из шапки: навигации в
              шапке больше нет (редизайн 2026-10, решение 26.10.2026). */}
          <Link
            ref={itemRef(0)}
            to={ROUTES.app}
            className={styles.item}
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              close(true);
            }}
          >
            <ListMagnifyingGlassIcon size={18} aria-hidden="true" />
            {t('nav.mySearches')}
          </Link>

          <Link
            ref={itemRef(1)}
            to={ROUTES.negotiationChannels}
            className={styles.item}
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              close(true);
            }}
          >
            <BellIcon size={18} aria-hidden="true" />
            {t('nav.channels')}
          </Link>

          <button
            ref={itemRef(2)}
            type="button"
            className={styles.item}
            role="menuitem"
            tabIndex={-1}
            disabled={logout.isPending}
            onClick={() => {
              logout.mutate(undefined, {
                onSettled: () => {
                  setOpen(false);
                },
              });
            }}
          >
            <SignOutIcon size={18} aria-hidden="true" />
            {logout.isPending ? t('auth.account.signingOut') : t('auth.account.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
